/**
 * "Your AI Office Manager" — 815 Digital Services brand mark.
 *
 * v1.5: renders the ACTUAL approved brand artwork supplied by 815 Digital
 * Services (cropped 1:1 from their brand sheet into two ready-to-use PNGs
 * in `/public/brand/`) rather than a recreated/approximated SVG. Per the
 * "Final Presentation Polish" directive: do not redesign or substitute —
 * use the supplied asset exactly as provided.
 *
 *   - `variant="square"` -> ai-office-manager-icon.png  (rounded-square
 *     "Primary Icon" tile — used for the floating launcher, the biggest,
 *     most-noticeable placement).
 *   - `variant="circle"` -> ai-office-manager-icon-small.png (the "Small
 *     Icon" circular mark — used anywhere compact: panel header, chat
 *     bubbles, tour dialog).
 *
 * Both source images already bake in their own black background + gold
 * ring/frame and two static gold sparkles, so this component never wraps
 * them in an extra colored background (that would double the frame).
 *
 * Sparkle overlays: transparent, absolutely-positioned "pop" stars are
 * layered on top of the artwork's own (static) sparkles so they can be
 * animated with CSS without touching the raster image itself.
 *   - `animated`  -> continuous twinkle loop (used while the AI is
 *     actively thinking/streaming a response).
 *   - `burst`     -> a single ~1.3s "pop" (used for the launcher's
 *     idle ambient pulse every 10-15s, and the brief flourish on open).
 */
const SPARKLE_PRESETS = {
  sm: [
    { top: "6%", right: "10%", size: 7 },
    { top: "24%", right: "1%", size: 5 },
  ],
  lg: [
    { top: "8%", right: "14%", size: 16 },
    { top: "27%", right: "4%", size: 10 },
  ],
};

const SparkleStar = () => (
  <svg viewBox="0 0 24 24" className="h-full w-full drop-shadow-[0_0_5px_hsl(var(--gold-bright)/0.85)]" aria-hidden="true">
    <path
      d="M12 0 L14.5 9.5 L24 12 L14.5 14.5 L12 24 L9.5 14.5 L0 12 L9.5 9.5 Z"
      fill="hsl(var(--gold-bright))"
    />
  </svg>
);

export const DriveDeskAIIcon = ({ className = "h-6 w-6", animated = false, burst = false, thinking = false, size = "sm", variant = "square" }) => {
  const src = variant === "circle" ? "/brand/ai-office-manager-icon-small.png" : "/brand/ai-office-manager-icon.png";
  const positions = SPARKLE_PRESETS[size] || SPARKLE_PRESETS.sm;
  const showSparkles = animated || burst || thinking;

  return (
    <span className={`relative inline-block shrink-0 ${className}`}>
      <img
        src={src}
        alt="Your AI Office Manager"
        className="h-full w-full object-contain select-none pointer-events-none"
        draggable="false"
      />
      {showSparkles &&
        positions.map((p, i) => {
          const animClass = burst
            ? "ai-sparkle-burst"
            : thinking
              ? i === 0 ? "ai-sparkle-thinking-a" : "ai-sparkle-thinking-b"
              : i === 0 ? "ai-sparkle-a" : "ai-sparkle-b";
          return (
            <span
              key={i}
              aria-hidden="true"
              className={`absolute ${animClass}`}
              style={{
                top: p.top,
                right: p.right,
                width: p.size,
                height: p.size,
                animationDelay: burst ? `${i * 140}ms` : undefined,
              }}
            >
              <SparkleStar />
            </span>
          );
        })}
    </span>
  );
};
